import {DataGetter, isAgenda, isItems} from "@t/fd/layout/Agenda";
import type {FormData} from "@t/fd/FormPart"
import {getter} from "../../Part";
import DataCell from "./DataCell";
import HeaderCell from "./HeaderCell.vue";
import SelectionHeaderCell from "./SelectionHeaderCell.vue";
import SelectionCell from "./SelectionCell.vue";
import {ComputedRef, CSSProperties, InjectionKey, onMounted, provide, reactive, ref, Ref, watch} from "vue";
import {computed, inject} from "vue";
import {Column, ColumnType, hasThisName} from "@t/agenda/Columns.ts";
import {DataGrid} from "@t/fd/layout/Agenda.ts";
import DeletionHeaderCell from "@fd/containers/agenda/DeletionHeaderCell.vue";
import FormEditorCell from "@fd/containers/agenda/FormEditorCell.vue";
import SortingHeaderCell from "@fd/containers/agenda/SortingHeaderCell.vue";
import SortingCell from "@fd/containers/agenda/SortingCell.vue";
import {COLOR_NAMES} from "@t/fd/editor/EventColor.ts";
import {injectEnv} from "@fd/Environment.ts";
import {AgendaData, AgendaMeta} from "@t/agenda/Agenda.ts";
import {NetworkResponse} from "@fd/Types.ts";
import {SortColumn} from "@t/agenda/Sort.ts";

/** Callback used to execute an action */
export type CellAction = (name:string,						// name of the action
							srcType:number,					// srcType (optional, may be 0)
							params:Record<string, any>,		// extracted params required to complete the action
							ctx: RowContext,				// record context
							event?:MouseEvent|PointerEvent	// optional mouse event if the action was executed as a result of user clicking
						) => void;

export type ColSortState = "no" /* Column is unsorted */
	| "asc" /* Column is sorted ascending */
	| "desc" /* Column is sorted descending */;

export interface ColSortInfo {
	/** True if the column supports sorting */
	supported: boolean;
	/** State of the column */
	state: ColSortState;
	/** Zero-based index of ORDER BY position. Available only if the state is "asc" or "desc" */
	order?: number;
}

export interface RowContext {
	/** Unique key to identify the row. Useful for rows which do not have primary key yet. */
	itemKey: number|string;
	/** Primary key of the record */
	primaryKey?: string;
	/** Index of the row */
	index: number;
	/** Record object */
	object?: object;
}

/**
 * Interface for cells to coordinate with agenda part
 */
export interface CellAPI {
    /** Return info if the column with this name is sorted and in what direction. null if the column wasn't found */
    getSorting: (colName:string) => ColSortInfo|null;
    setSorting: (colName:string, type: ColSortState | "toggle", append?:boolean) => void;
	action: CellAction;
}

/** Key to get DataGrid object */
export const itemsGridKey = Symbol() as InjectionKey<ComputedRef<DataGrid>>;
export const rowContextKey = Symbol() as InjectionKey<ComputedRef<RowContext>>;

/** Key to get Grid-Cell API */
export const cellApiKey = Symbol() as InjectionKey<CellAPI>;


export function injectItemsGrid():ComputedRef<DataGrid> {
    const ap = inject(itemsGridKey);
    if (ap === undefined) throw "Error injecting agenda/items (probably a bug)";
    return ap;
}

export function injectCellAPI():CellAPI {
    const capi = inject(cellApiKey);
    if (capi === undefined) throw "Cell API not provided (internal bug)";
    return capi;
}

export interface RowGroupStructure {
	/** Total number of columns */
	cols: number;
	colStyles?: CSSProperties[];
	tableStyle?: CSSProperties;

	/** Structure of a regular row */
	row: RowStructure[];
	/** Structure of a header row */
	header: RowStructure[];
	/** Structure of an editor row */
	editor?: RowStructure[];
}

export interface RowStructure {
    cells: CellStructure[]
}

export interface CellBinding {
	[key:string]:any;
}

export interface CellStructure {
	/** How much space the cell takes*/
    rowspan: number;
    colspan: number;
	/** What component is responsible for generating content */
	component: any;
	/** Binding props for the component */
	bind?: CellBinding;
	/** True if the cell isn't last cell of the row structure and should not have any bottom border */
	notBottom: boolean;
	/** True if this cell is at the left border of the row group */
	notLeft: boolean;
	/** True if this cell is at the left border of the row group */
	notRight: boolean;
}

export function findColumnOf(colName:string|undefined|null, all:Column[]):Column|null {
    if (colName == null || all.length === 0) return null;
	for (let c of all) {
		if (hasThisName(c, colName)) return c;
	}
	return null;
}

function bind<T extends Partial<CellStructure>>(cc:T, key:string, value: any):T {
	if (typeof cc.bind === "undefined") cc.bind = {};
	cc.bind[key] = value;
	return cc;
}


const SELECTION:Column = {
	name: "_selection",
	title: "Select",
	thitle: "", icon: "selection",
	type: ColumnType.SELECT,
	canSort: false,
	layout: "LEFT",
	fixedWidth: "20px", /* Set to the size of the select icon */
	jsonGetters: []
};

const DELETION:Column = {
	name: "_deletion",
	title: "Delete",
	thitle: "", icon: "trash",
	type: ColumnType.DELETE,
	canSort: false,
	layout: "RIGHT",
	fixedWidth: "18px", /* Set to the size of the trash icon */
	jsonGetters: []
};

const SORTING:Column = {
	name: "_sorting",
	title: "Sort",
	thitle: "", icon: "reorder",
	type: ColumnType.SORT,
	canSort: false,
	layout: "RIGHT",
	fixedWidth: "22px", /* Set to the size of the menu-18 icon */
	jsonGetters: []
};

function buildRowCell(c:Column, colspan:number = 1, rowspan:number = 1):CellStructure {
	let component:any;
	let bind:CellBinding = { col: c };

	if (c.type === ColumnType.SELECT) {
		component = SelectionCell;
	} else if (c.type === ColumnType.SORT) {
		component = SortingCell;
	} else {
		// Data column
		component = DataCell;
	}

	return {
		colspan: colspan,
		rowspan: rowspan,
		notBottom: false,
		notLeft: false,
		notRight: false,
		component,
		bind
	};
}

function rowEditorCell(colspan:number, readonly: boolean):CellStructure {
	return {
		colspan: colspan,
		rowspan: 1,
		component: FormEditorCell,
		bind: { tag:"td", readonly },
		notBottom: false,
		notLeft: false,
		notRight: false
	};
}


function buildHeaderCell(c:Column, colspan:number = 1, rowspan:number = 1):CellStructure {
	let component:any;
	let bind:CellBinding|undefined;

	if (c.type === ColumnType.SELECT) {
		component = SelectionHeaderCell;
		bind = { col: c };
	} else if (c.type === ColumnType.DELETE) {
		component = DeletionHeaderCell;
		bind = { col: c };
	} else if (c.type === ColumnType.SORT) {
		component = SortingHeaderCell;
		bind = { col: c };
	} else {
		// Data column
		component = HeaderCell;
		bind = { col: c };
	}

	return {
		colspan: colspan,
		rowspan: rowspan,
		component,
		bind,
		notBottom: false,
		notLeft: false,
		notRight: false
	};
}


export function calculateStructure(cols: Column[], readonly = false):RowGroupStructure {

	// Separate columns which stick to left/right/top/bottom
	const left:Column[] = [];
	const right:Column[] = [];
	const top:Column[] = [];
	const bottom:Column[] = [];
	const normal:Column[] = [];

	let hasFixedColumn = false;

	const sorter = (col:Column) => {
		if (col.layout) {
			switch (col.layout) {
				case "LEFT":
					left.push(col);
					break;
				case "RIGHT":
					right.push(col);
					break;
				case "TOP":
					top.push(col);
					break;
				case "BOTTOM":
					bottom.push(col);
					break;
				default:
					normal.push(col);
			}
		} else normal.push(col);
	};

	for (let col of cols) {
		sorter(col);
	}

	// Do we need to break normal columns into multiple runs
	let runs:Column[][] = [ normal ];
	let normalCount = normal.length;

	// Insert top columns to runs
	for (let i = 0; i < top.length; i++) {
		runs.splice(i, 0, [ top[i] ]);
	}
	// Append bottom columns to runs
	for (let b of bottom) {
		runs.push([ b ]);
	}

	//----
    const rowStructure:RowStructure[] = [];
    const headerStructure:RowStructure[] = [];
    const editorStructure:RowStructure[] = [];

	let first:boolean = true;
	for (let r of runs) {
		const rowCells:CellStructure[] = [];
		const headerCells:CellStructure[] = [];
		const editorCells:CellStructure[] = [];
		if (first) {
			// Insert sticky left columns
			for (let c of left) {
				headerCells.push(buildHeaderCell(c, 1, runs.length));
				let rc = buildRowCell(c, 1, runs.length);
				rowCells.push(rc);
				editorCells.push({ ...rc, rowspan:1 });
			}
		}
		let needSpan = normalCount;
		for (let i = 0; i < r.length; i++) {
			const span =  i == (r.length - 1) ? needSpan : 1;	// last column takes the rest
			const c = r[i];
			rowCells.push(buildRowCell(c, span));
			headerCells.push(buildHeaderCell(c, span));
			needSpan -= span;
		}
		if (first) editorCells.push(rowEditorCell(normalCount, readonly));

		if (first) {
			// Insert sticky right columns
			for (let c of right) {
				headerCells.push(buildHeaderCell(c, 1, runs.length));
				let rc = buildRowCell(c, 1, runs.length);
				rowCells.push(rc);
				editorCells.push({ ...rc, rowspan:1 });
			}
		}
		rowStructure.push({ cells: rowCells });
		headerStructure.push({ cells: headerCells });
		if (first) {
			editorStructure.push({ cells: editorCells });
			first = false;
		}
	}
	let result:RowGroupStructure = { row: setCellFlags(rowStructure), header: setCellFlags(headerStructure), editor: setCellFlags(editorStructure), cols: normalCount + left.length + right.length };
	result.colStyles = generateColumnStyles(left, normal, right);
	return result;
}

function setCellFlags(group:RowStructure[]):RowStructure[] {
	const height = group.length;
	if (height === 0) return group;

	// First pass: vertical flags (inCell)
	for (let row = 0; row < height; row++) {
		let rs = group[row];
		if (rs.cells.length > 0) {
			for (let cell of rs.cells) {
				// cell is not ending in this row -> it continues vertically
				if (cell.rowspan + row < height) cell.notBottom = true;
			}
			rs.cells[0].notLeft = true;
			rs.cells[rs.cells.length - 1].notRight = true;
		}
	}

	let row = 0;
	while (row < height) {
		const rs:RowStructure = group[row];
		if (rs.cells.length > 0) {
			const firstCell = rs.cells[0];
			firstCell.notLeft = false;
			row += firstCell.rowspan;
		} else row++;
	}

	row = 0;
	while (row < height) {
		const rs:RowStructure = group[row];
		if (rs.cells.length > 0) {
			const lastCell = rs.cells[rs.cells.length - 1];
			lastCell.notRight = false;
			row += lastCell.rowspan;
		} else row++;
	}

	return group;
}

function generateColumnStyles(left:Column[], normal:Column[], right:Column[]):CSSProperties[] {
	const styles: CSSProperties[] = [];
	const cols = [...left, ...normal, ...right];
	let emit = false;
	for (let c of cols) {
		const s:CSSProperties = {};
		if (c.fixedWidth) {
			s.width = "calc("+c.fixedWidth+" + 2 * var(--agenda-h-padding))";
			emit = true;
		}
		styles.push(s);
	}
	return emit ? styles : undefined;
}

export function cellStyle(col:Column):CSSProperties {
	const s: CSSProperties = {};
	switch (col.justify) {
		case "LEFT":
			s.textAlign = "left";
			break;
		case "CENTER":
			s.textAlign = "center";
			break;
		case "RIGHT":
			s.textAlign = "right";
			break;
	}
	switch (col.verticalAlign) {
		case "TOP":
			s.verticalAlign = "top";
			break;
		case "BASELINE":
			s.verticalAlign = "baseline";
			break;
		case "MIDDLE":
			s.verticalAlign = "middle";
			break;
		case "BOTTOM":
			s.verticalAlign = "bottom";
			break;
	}
	switch (col.size) {
		case "SMALL":
			s.fontSize = "var(--font-size-s)";
			break;
		case "NORMAL":
			s.fontSize = "var(--font-size)";
			break;
		case "LARGE":
			s.fontSize = "var(--font-size-l)";
			break;
	}
	switch (col.weight) {
		case "THIN":
			s.fontWeight = "300";
			break;
		case "NORMAL":
			s.fontWeight = "normal";
			break;
		case "THICK":
			s.fontWeight = "500";
			break;
		case "BOLD":
			s.fontWeight = "bold";
			break;
	}
	if (col.fg) {
		const name = COLOR_NAMES[col.fg];
		if (name) s.color = "var(--" + name + "-fg)";
	}
	if (col.bg) {
		const name = COLOR_NAMES[col.bg];
		if (name) s.backgroundColor = "var(--" + name + "-bg)";
	}

	return s;
}

function isDynamic(rule:DataGetter):rule is DataGetter {
    return rule.hasOwnProperty("d");
}

export function extractDataVariable(obj: any, g: DataGetter, formData: FormData):any {
    if (typeof g === "object") {
        let val: any;
        if (g.d) {
            val = getter(obj, g.d, formData);
            if (val === undefined) {
                val = g.value;
            }
        } else {
            val = g.value;
        }
        return val;
    } else {
        return g;    // static value directly
    }
}

export function cellData(obj:any, col:Column, formData:FormData):any[] {
    const output:any[] = [];
    if (Array.isArray(col.jsonGetters)) {
        for (let g of col.jsonGetters) {
            output.push(extractDataVariable(obj, g, formData));
        }
    } else {
        // implicit strategy for obtaining data (property equals to column name)
        output.push(getter(obj, col.name, formData));
    }
    return output;
}

export const AGENDA_START_PADDING = 48;	// 2 x --form-h-start-padding

export function useDataGrid(grid:ComputedRef<DataGrid>, readonly:ComputedRef<boolean>,
							localData:ComputedRef<FormData>, formData:any, action:CellAction) {

	const env = injectEnv();
	provide(itemsGridKey, grid);
	const meta = ref<AgendaMeta>(null);
	const downloadedRows = ref<any[]>([]);
	const position = ref<number>(0);
	const total = ref<number>(0);
	const reloadCounter = ref<number>(0);


	async function loadAgendaMeta():Promise<AgendaMeta> {
		if (grid.value.meta) return grid.value.meta;
		const nr = await env.net.fetch(agendaEndpoint.value, "META");
		if (nr.ok) {
			return await nr.json() as AgendaMeta;
		} else {
			console.error("Failed to fetch agenda metadata from " + agendaEndpoint.value + ", status="+nr.status);
			return {
				view: { columns:[] },
				page: 20,
				allowedQuery: false,
				allColumns: []
			};
		}
	}

	onMounted(async () => {
		let m = await loadAgendaMeta();
		if (typeof m.position === 'number') position.value = m.position;
		meta.value = m;
	});

	const pageSize = computed<number>(() => {
		let size:number;
		if (typeof meta.value?.page === 'number') size = meta.value.page;
		else size = 20;
		return size;
	});

	const agendaEndpoint = computed<string>(() => {
		const dg:DataGrid = grid.value;
		if (isAgenda(dg)) {
			return dg.url;	// TODO apply params to the URL
		}
		return null;
	});

	/** Raw endpoint + paging query params */
	const pagedEndpoint = computed<string|null>(() => {
		let ep = agendaEndpoint.value;
		if (ep == null || meta.value == null) return null;
		ep = env.net.urlParam(ep, "size", pageSize.value);
		ep = env.net.urlParam(ep, "position", position.value);
		const ordering = meta.value.view?.sort;
		if (Array.isArray(ordering) && ordering.length > 0) {
			ep = env.net.urlParam(ep, "orderBy", ordering[0].column);
			ep = env.net.urlParam(ep, "desc", (ordering[0].desc).toString());
		}
		return ep;
	});

	const array = computed<any[]>(() => {
		if (grid.value.property) {
			// Array of items is part of data object
			const v = getter(localData.value as FormData, grid.value.property, formData);
			return Array.isArray(v) ? v : [];
		} else {
			return downloadedRows.value;
		}
	});

	watch([pagedEndpoint, reloadCounter, () => grid.value.property], async () => {
		if (!grid.value.property && pagedEndpoint.value) {
			let nr:NetworkResponse;
			if (meta.value.allowedQuery && meta.value?.view?.filter) {
				nr = await env.net.fetch(pagedEndpoint.value, "QUERY", meta.value.view.filter);
			} else {
				nr = await env.net.fetch(pagedEndpoint.value, "GET");
			}
			if (nr.ok) {
				const d:AgendaData<any> = await nr.json() as AgendaData<any>;
				downloadedRows.value.length = 0;
				for (let item of d.items) downloadedRows.value.push(item);
				total.value = d.total;
			} else {
				console.error("Cannot load data from " + pagedEndpoint.value + ", status=" + nr.status);
			}
		}
	}, { immediate: true });

	function dirty() {
		reloadCounter.value++;
	}

	/** Columns selectable by the view (usually data columns) */
	const dataColumns = computed<Column[]>(() => {
		return meta.value.allColumns;
	});

	/** Columns which are always displayed (usually control columns for selection, deletion, ...) */
	const fixedColumns = computed<Column[]>(() => {
		let fc = grid.value.fixedColumns || [];
		if (grid.value.selectable) fc.push(SELECTION);
		if (isItems(grid.value) && grid.value.sortable) fc.push(SORTING);
		return fc;
	});

	const columns = computed<Column[]>(() => {
		const vc:Column[] = [...fixedColumns.value];
		if (Array.isArray(meta.value?.view?.columns)) {
			for (let colName of meta.value.view.columns) {
				const found = findColumnOf(colName, dataColumns.value);
				if (found != null) vc.push(found);
			}
			return vc;
		}
		return vc;
	});

	const structure = computed<RowGroupStructure>(() => {
		//console.log("Building row structure: " + readonly.value);
		return calculateStructure(columns.value, readonly.value);
	});


	function cellBind(c:CellStructure) {
		const o:any = c.bind ? { ...c.bind } : {};
		if (c.rowspan > 1) o.rowspan = c.rowspan;
		if (c.colspan > 1) o.colspan = c.colspan;
		const classes:string[] = [];
		if (c.notBottom) classes.push("not-bottom");
		if (c.notLeft) classes.push("not-left");
		if (c.notRight) classes.push("not-right");
		if (classes.length > 0) o['class'] = classes;
		return o;
	}

	function findColumn(colName: string):Column|null {
		for (let c of columns.value) {
			if (hasThisName(c, colName)) return c;
		}
		return null;
	}

	function getSorting(colName: string): ColSortInfo {
		const col: Column = findColumn(colName);
		if (col == null) {
			console.warn("getSorting for non-existing column " + colName);
			return { supported:false, state: "no" };
		}
		if (col.canSort) {
			const m = meta.value;
			if (m?.view?.sort) {
				for (let i = 0; i < m.view.sort.length; i++) {
					const sc: SortColumn = m.view.sort[i];
					if (hasThisName(col, sc.column)) {
						return { supported: true, state: sc.desc ? "desc":"asc", order: i };
					}
				}
				return { supported: true, state: "no" };
			}
		} else {
			return { supported: false, state: "no" };
		}
	}

	function setSorting(colName: string, type: ColSortState | "toggle", append = false): void {
		const col: Column = findColumn(colName);
		if (col == null) return;
		if (col.canSort) {
			const m = meta.value;
			if (!m.view) m.view = {};
			if (!m.view.sort) m.view.sort = [];
			for (let i = 0; i < m.view.sort.length; i++) {
				const sc: SortColumn = m.view.sort[i];
				if (hasThisName(col, sc.column)) {
					// The column is part of sort already
					switch (type) {
						case "no":
							m.view.sort.splice(i, 1);
							return;
						case "asc":
							sc.desc = false;
							return;
						case "desc":
							sc.desc = true;
							return;
						case "toggle":
							sc.desc = !sc.desc;
							return;
					}
					throw new Error("Invalid sort type = "+type);
				}
			}
			// This column is NOT part of sort
			m.view.sort.length = 0;
			if (type === "no") return;	// nothing to do
			m.view.sort.push({ column: col.fullName || col.alias || col.name, desc: (type === "desc") });
		}
	}


	function primaryKeyOf(o:any):string|null {
		const formData = {};	// not relevant for identity.
		let dataGetter:DataGetter = grid.value.identity;
		if (!dataGetter) dataGetter = { d: "id" };	// expect id property as a default identity property
		let dataVal = extractDataVariable(o, dataGetter, formData);
		return dataVal == null ? null : dataVal.toString();
	}


	provide(cellApiKey, { getSorting, setSorting, action });


	return { structure, columns, cellBind, array, position, total, pageSize, primaryKeyOf, dirty };
}